import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, HostListener, ElementRef, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { takeUntil, switchMap, map } from 'rxjs/operators';
import * as _ from 'lodash';

import { TasksService } from '../../../../pages/tasks/services/tasks.service';
import { LoggerService } from '../../../../services/logger.service';
import { NotificationUtilityService } from '../../../../services/notification-utility.service';
import { NotificationRow } from '../../../../shared/models/notification.model';

@Component({
    selector: 'app-notification-drawer',
    templateUrl: './notification-drawer.component.html',
    styleUrls: ['./notification-drawer.component.scss'],
    standalone: false
})
export class NotificationDrawerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  @Output() onClose = new EventEmitter<void>();
  @Output() onRead = new EventEmitter<NotificationRow>();
  @Output() onReadAll = new EventEmitter<void>();

  listingNotificationAll: NotificationRow[] = [];
  loadingNotificaitonsAll = false;
  notificationPageAll = 1;
  notificationLimitAll = 20;
  hasMoreNotificationsAll = true;
  notifictionCountAll: number = 0;
  
  merchantId: string;
  imageUrl: string = ''; // Should be passed or fetched if needed, but header has it

  // Shared variables from Header for navigation logic
  viewOrganizationArr: any;
  customerLeadDetailsArr: any;
  templatePayload: any;
  selectedContact: any;

  constructor(
    private tasksService: TasksService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    public notificationUtility: NotificationUtilityService,
    private translateService: TranslateService
  ) {
    this.merchantId = localStorage.getItem('merchant_id');
  }

  ngOnInit(): void {
    this.viewAllNotificationListing();
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  viewAllNotificationListing() {
    if (!this.loadingNotificaitonsAll && this.hasMoreNotificationsAll) {
      this.loadingNotificaitonsAll = true;
      const payload = {
        viewAll: true,
        page: this.notificationPageAll,
        limit: this.notificationLimitAll
      };
      
      this.tasksService.notificationListing(payload).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          const incomingData: NotificationRow[] = res.body?.data?.rows ?? res?.data?.rows ?? [];
          const getUniqueKey = (n: NotificationRow) => `${n.description}_${n.module}_${n.moduleId}`;

          if (this.notificationPageAll === 1) {
            const seenKeys = new Set();
            this.listingNotificationAll = incomingData.filter(n => {
              const key = getUniqueKey(n);
              if (key && !seenKeys.has(key)) {
                seenKeys.add(key);
                return true;
              }
              return false;
            });
          } else {
            const existingKeys = new Set(this.listingNotificationAll.map(n => getUniqueKey(n)));
            const uniqueNewData = incomingData.filter(n => {
              const key = getUniqueKey(n);
              if (key && !existingKeys.has(key)) {
                existingKeys.add(key);
                return true;
              }
              return false;
            });
            this.listingNotificationAll = [...this.listingNotificationAll, ...uniqueNewData];
          }

          const notificationCountIntAll = res.body?.data?.count ?? res?.data?.count ?? 0;
          this.notifictionCountAll = notificationCountIntAll;
          
          if (incomingData.length < this.notificationLimitAll) {
            this.hasMoreNotificationsAll = false;
          }

          this.loadingNotificaitonsAll = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.logger.error('Error fetching all notifications:', err);
          this.loadingNotificaitonsAll = false;
        }
      });
    }    
  }

  onNotificationScroll(event: Event) {
    if (!event.target) return;
    const element = event.target as HTMLElement;
    if (!this.loadingNotificaitonsAll && this.hasMoreNotificationsAll && 
        element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
        this.notificationPageAll++;
        this.viewAllNotificationListing();
    }
  }

  trackByNotification(index: number, item: NotificationRow): string | number {
    return item.id;
  }

  notificationReadAll() {
    this.tasksService.notificationRead({
      "type": "update",
      "id": "all"
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: { status: string }) => {
        this.listingNotificationAll = [];
        this.onReadAll.emit();
      },
      error: (err) => {
        this.logger.error('Error marking all notifications as read:', err);
      }
    });
  }

  /**
   * Emits the notification read event to the parent HeaderComponent.
   * Validates the data structure before emission to ensure stability and satisfy strict analysis.
   */
  notificationRead(data: NotificationRow) {
    if (!data || !data.id || !data.module) {
      this.logger.warn('Attempted to read notification with invalid or incomplete data', { data });
      return;
    }

    // Only emit the event if valid. The Parent (HeaderComponent) handles the
    // API call and navigation logic for persistence.
    this.onRead.emit(data);
  }

  // For any notification whose description contains an embedded <a> link, strip
  // the anchor and trailing "Click"/"here" word so the template can render a
  // clean text + separate, real Angular-bound "Click" link.
  hasEmbeddedLink(description: string): boolean {
    return !!description && description.includes('<a');
  }

  isNewQuoteRequest(description: string): boolean {
    if (!description) return false;
    const lower = description.toLowerCase();
    if (
      lower.includes('accepted') ||
      lower.includes('quoted') ||
      lower.includes('rejected') ||
      lower.includes('quote is') ||
      lower.includes('when quote') ||
      lower.includes('order') ||
      lower.includes('invoice') ||
      lower.includes('converted') ||
      lower.includes('marked')
    ) {
      return false;
    }
    return true;
  }

  getNotificationText(description: string): string {
    if (!description) return '';
    return description
      .replace(/<a[^>]*>.*?<\/a>/i, '')
      .replace(/\b(Click|here)\b/gi, '')
      .replace(/\.\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getTranslatedText(key: string): string {
    const translation = this.translateService.instant(key);
    return translation !== key ? translation : '';
  }

  translateNotificationDescription(description: string): string {
    if (!description) return '';
    return (
      this.getTranslatedText(description) ||
      this.getTranslatedText('COMMON.' + description) ||
      description
    );
  }

  getTranslatedNotificationText(description: string): string {
    const text = this.getNotificationText(description);
    if (!text) return '';
    return (
      this.getTranslatedText(text) ||
      this.getTranslatedText('COMMON.' + text) ||
      text
    );
  }
  translateTimeago(text: string): string {
    return this.notificationUtility.translateTimeago(text);
  }

  closeDrawer() {
    this.onClose.emit();
  }
}
